import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  activateNotificationTemplate,
  createNotificationTemplate,
  deactivateNotificationTemplate,
  getNotificationTemplates,
  updateNotificationTemplate,
} from '../api/notificationsApi';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';

const { semantic } = colorTokens;

const emptyForm = {
  key: '',
  name: '',
  category: 'system',
  default_channels: 'in_app',
  subject_template: '',
  body_template: '',
  is_active: true,
};

const rowActionSx = {
  minHeight: 32,
  px: 1,
};

const deactivateOutlineSx = {
  ...rowActionSx,
  color: semantic.error,
  border: `1px solid ${semantic.error}`,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'rgba(211, 47, 47, 0.08)',
    borderColor: semantic.error,
    color: semantic.error,
  },
};

const dialogPaperSx = {
  width: 'min(640px, calc(100vw - 48px))',
  maxWidth: 'min(640px, calc(100vw - 48px))',
  maxHeight: 'calc(100vh - 64px)',
  bgcolor: semantic.bg.surface,
  backgroundImage: 'none',
  border: `1px solid ${semantic.border.subtle}`,
  borderRadius: `${radiusTokens.card}px`,
};

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

export function NotificationTemplatesPage() {
  const { t } = useTranslation(['notifications', 'common']);
  const { session } = useAuth();
  const canManage = hasPermission(session, 'system.notification_templates.manage');

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const emptyValue = t('common:empty_value');
  const recordCount = templates.length;

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getNotificationTemplates({ per_page: 100 });
      setTemplates(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = useCallback((template) => {
    setEditing(template);
    setForm({
      key: template.key,
      name: template.name,
      category: template.category || 'system',
      default_channels: (template.default_channels || ['in_app']).join(', '),
      subject_template: template.subject_template || '',
      body_template: template.body_template || '',
      is_active: template.is_active,
    });
    setDialogOpen(true);
  }, []);

  const handleSave = async () => {
    setSaving(true);

    const payload = {
      key: form.key,
      name: form.name,
      category: form.category,
      default_channels: form.default_channels
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      subject_template: form.subject_template,
      body_template: form.body_template,
      is_active: form.is_active,
    };

    try {
      if (editing) {
        await updateNotificationTemplate(editing.id, payload);
      } else {
        await createNotificationTemplate(payload);
      }

      setDialogOpen(false);
      await loadTemplates();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = useCallback(async (template) => {
    if (template.is_active) {
      await deactivateNotificationTemplate(template.id);
    } else {
      await activateNotificationTemplate(template.id);
    }

    await loadTemplates();
  }, [loadTemplates]);

  const columns = useMemo(() => [
    {
      id: 'key',
      label: t('common:key'),
      render: (row) => (
        row.key
          ? <Typography sx={monoSx}>{row.key}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'name',
      label: t('common:name'),
      render: (row) => row.name || emptyValue,
    },
    {
      id: 'category',
      label: t('common:category'),
      render: (row) => (
        row.category
          ? (
            <StatusBadge
              status={row.category}
              category="generic"
              apiLabel={row.category}
              size="Sm"
              tone="Neutral"
            />
          )
          : emptyValue
      ),
    },
    {
      id: 'is_active',
      label: t('common:active_label'),
      render: (row) => (
        <StatusBadge
          status={row.is_active ? 'active' : 'inactive'}
          category="generic"
          apiLabel={row.is_active ? t('notifications:templates.active') : t('notifications:templates.inactive')}
          size="Sm"
          tone={row.is_active ? 'Success' : 'Neutral'}
        />
      ),
    },
    {
      id: 'default_channels',
      label: t('notifications:templates.channels'),
      render: (row) => {
        const channels = (row.default_channels || []).join(', ');
        return channels
          ? <Typography sx={monoSx}>{channels}</Typography>
          : emptyValue;
      },
    },
    {
      id: 'actions',
      label: t('common:actions'),
      align: 'right',
      render: (row) => {
        if (!canManage) {
          return emptyValue;
        }

        return (
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            justifyContent="flex-end"
            alignItems="center"
            sx={{ whiteSpace: 'nowrap' }}
          >
            <Button variant="Ghost" onClick={() => openEdit(row)} sx={rowActionSx}>
              {t('common:edit')}
            </Button>
            {row.is_active ? (
              <Button variant="Ghost" onClick={() => handleToggleActive(row)} sx={deactivateOutlineSx}>
                {t('common:deactivate')}
              </Button>
            ) : (
              <Button variant="Secondary" onClick={() => handleToggleActive(row)} sx={rowActionSx}>
                {t('common:activate')}
              </Button>
            )}
          </Stack>
        );
      },
    },
  ], [canManage, emptyValue, handleToggleActive, openEdit, t]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('notifications:templates.title')}
        subtitle={t('notifications:templates.subtitle')}
        actions={canManage ? (
          <Button variant="Accent" onClick={openCreate}>
            {t('notifications:templates.new_template')}
          </Button>
        ) : null}
      />

      <Alert severity="warning" sx={{ mb: 2 }}>
        {t('notifications:templatesInternalNotice')}
      </Alert>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('notifications:templatesDeferredNotice')}
      </Alert>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadTemplates} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('notifications:templates.load_failed')}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('notifications:templates.templates')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('notifications:templates.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : !error ? (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={templates}
            getRowKey={(row) => row.id}
            emptyMessage={t('notifications:templates.no_templates')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        ) : null}
      </SectionCard>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (!saving) setDialogOpen(false);
        }}
        maxWidth={false}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 3,
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 16,
            color: semantic.text.primary,
            borderBottom: `1px solid ${semantic.border.subtle}`,
          }}
        >
          {editing
            ? t('notifications:templates.edit_template')
            : t('notifications:templates.create_template')}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1, overflowY: 'auto' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextInput
              density="Compact"
              label={t('common:key')}
              value={form.key}
              disabled={Boolean(editing) || saving}
              onChange={(e) => setForm((prev) => ({ ...prev, key: e.target.value }))}
            />
            <TextInput
              density="Compact"
              label={t('common:name')}
              value={form.name}
              disabled={saving}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <TextInput
              density="Compact"
              label={t('common:category')}
              value={form.category}
              disabled={saving}
              onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
            />
            <TextInput
              density="Compact"
              label={t('notifications:templates.default_channels')}
              value={form.default_channels}
              disabled={saving}
              onChange={(e) => setForm((prev) => ({ ...prev, default_channels: e.target.value }))}
            />
            <TextInput
              density="Compact"
              label={t('notifications:templates.subject_template')}
              value={form.subject_template}
              disabled={saving}
              onChange={(e) => setForm((prev) => ({ ...prev, subject_template: e.target.value }))}
            />
            <TextInput
              density="Compact"
              label={t('notifications:templates.body_template')}
              value={form.body_template}
              disabled={saving}
              onChange={(e) => setForm((prev) => ({ ...prev, body_template: e.target.value }))}
              multiline
              minRows={5}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 140,
                  alignItems: 'flex-start',
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="Ghost"
            disabled={saving}
            onClick={() => setDialogOpen(false)}
          >
            {t('common:cancel')}
          </Button>
          <Button
            variant="Accent"
            loading={saving}
            disabled={saving}
            onClick={handleSave}
          >
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
